(function () {

    angular
        .module('app')
        .service('authService', authService);

    var angDependencies = [
        'authManager', 'loggerService', 'storageService', 'browserStorageService',
        '$q', '$http',
        '$mdDialog',
        '$rootScope',
        '$window',
        '$state',
        'qitanAiContractService',
        '$timeout'
    ];
    if (AUTH0_CLIENT_ID) {
        angDependencies.push('lock');
    }

    authService.$inject = angDependencies;

    function authService(authManager, loggerService, storageService, browserStorageService, $q, $http, $mdDialog, $rootScope, $window, $state, qitanAiContractService, $timeout, lock) {

        var SESSION_USERS_CLASS = 'session-users';
        var PRODUCT_ROLE_DEFAULT_ROUTES = {
            teacher : '/#!/teacher',
            student : '/#!/projects',
            orgadmin : '/#!/org-admin/overview',
            platform_ops : '/#!/siteadmin'
        };
        var DEFAULT_STATE_BY_ROUTE = {
            '/#!/teacher' : 'teacher',
            '/#!/projects' : 'projects',
            '/#!/org-admin/overview' : 'org_admin_transition',
            '/#!/siteadmin' : 'siteadmin'
        };

        // To avoid a session ever expiring, the UI will try to silently refresh
        //  access tokens in the background.
        // We'll do this 10 minutes before the token expires, to avoid risking a
        //  timing window where we're too late to do it.
        // This constant is the time before the token expiry when we'll try doing it.
        var TEN_MINUTES_MILLISECS = 600000;

        var vm = this;
        var productContext = null;
        var productContextPromise = null;
        var productContextGeneration = 0;

        // If the user is logged in as a registered user, we'll try and refresh
        //  their access token for them before it expires, to prevent them from
        //  being logged off while active.
        // This timer is used to do that.
        var nextRefreshTimer = null;

        $rootScope.isTeacher = false;
        $rootScope.isSiteAdmin = false;
        $rootScope.isOrgAdmin = false;
        $rootScope.isAuthenticated = false;
        $rootScope.productRole = null;

        loggerService.debug('[ml4kauth] init');
        storageService.confirmLocalStorage();

        function isOidcLoginEnabled() {
            return typeof OIDC_LOGIN_ENABLED !== 'undefined' && OIDC_LOGIN_ENABLED;
        }

        function isLocalPreviewPasswordLoginEnabled() {
            return typeof LOCAL_PREVIEW_PASSWORD_LOGIN_ENABLED !== 'undefined' &&
                   LOCAL_PREVIEW_PASSWORD_LOGIN_ENABLED;
        }

        function isLocalPreviewHost() {
            return $window.location.hostname === '127.0.0.1' ||
                   $window.location.hostname === 'localhost' ||
                   $window.location.hostname === '[::1]';
        }

        function createProductContextError(message, status) {
            var err = new Error(message);
            err.status = status || 403;
            err.productContextInvalid = true;
            return err;
        }

        function normalizeServerProductContext(meResponse) {
            if (!meResponse || typeof meResponse !== 'object' ||
                typeof meResponse.actorId !== 'string' || !meResponse.actorId ||
                !Array.isArray(meResponse.classes))
            {
                throw createProductContextError('Invalid /api/me response');
            }

            // Actor IDs are opaque, case-sensitive identifiers. Normalizing them
            // could alias two principals, so the server and stored login must match exactly.
            if (!userProfile || meResponse.actorId !== userProfile.user_id) {
                throw createProductContextError('Invalid /api/me actor');
            }

            var expectedDefaultRoute = PRODUCT_ROLE_DEFAULT_ROUTES[meResponse.role];
            if (!expectedDefaultRoute || meResponse.defaultRoute !== expectedDefaultRoute) {
                throw createProductContextError('Invalid /api/me role or default route');
            }

            return {
                actorId : meResponse.actorId,
                productRole : meResponse.role,
                defaultRoute : meResponse.defaultRoute,
                organization : meResponse.organization,
                classes : meResponse.classes
            };
        }

        function applyProductContext(context) {
            productContext = context;
            $rootScope.productRole = context.productRole;
            $rootScope.isOrgAdmin = context.productRole === 'orgadmin';
            return context;
        }

        function resetProductContext() {
            productContextGeneration += 1;
            productContext = null;
            productContextPromise = null;
            $rootScope.productRole = null;
            $rootScope.isOrgAdmin = false;
        }

        function getCurrentProductContext() {
            return productContext;
        }

        function getProductContext() {
            if (productContext) {
                return $q.when(productContext);
            }
            if (productContextPromise) {
                return productContextPromise;
            }
            if (!userProfile || !isAuthenticated()) {
                return $q.reject(createProductContextError('Authentication required', 401));
            }

            var requestGeneration = productContextGeneration;
            var sessionReady = isOidcLoginEnabled() &&
                !shouldUseLocalPreviewAuth() &&
                userProfile.tenant !== SESSION_USERS_CLASS ?
                requirePlatformSessionCookie() :
                $q.when();
            var requestPromise = sessionReady
                .then(function () {
                    return $http.get(qitanAiContractService.getAuthApiUrl('me'));
                })
                .then(function (resp) {
                    if (requestGeneration !== productContextGeneration) {
                        throw createProductContextError('Authentication changed while loading /api/me', 401);
                    }
                    return applyProductContext(normalizeServerProductContext(resp.data));
                })
                .catch(function (err) {
                    if (requestGeneration === productContextGeneration &&
                        productContextPromise === requestPromise &&
                        err && (
                        err.status === 401 ||
                        err.status === 403 ||
                        err.productContextInvalid === true
                    )) {
                        clearAuthData();
                    }
                    throw err;
                })
                .finally(function () {
                    if (requestGeneration === productContextGeneration &&
                        productContextPromise === requestPromise)
                    {
                        productContextPromise = null;
                    }
                });
            productContextPromise = requestPromise;
            return requestPromise;
        }

        function getDefaultStateName(context) {
            if (!context ||
                PRODUCT_ROLE_DEFAULT_ROUTES[context.productRole] !== context.defaultRoute ||
                !DEFAULT_STATE_BY_ROUTE[context.defaultRoute])
            {
                throw createProductContextError('Invalid default route');
            }
            return DEFAULT_STATE_BY_ROUTE[context.defaultRoute];
        }

        function goToPostLoginState(postLoginState) {
            if (typeof postLoginState === 'string') {
                return $state.go(postLoginState);
            }
            return $state.go(postLoginState.name, postLoginState.params || {});
        }

        function continueAfterLogin() {
            return getProductContext()
                .then(function (context) {
                    var postLoginState = consumePostLoginState();
                    if (postLoginState) {
                        return goToPostLoginState(postLoginState);
                    }
                    return $state.go(getDefaultStateName(context));
                });
        }

        var userProfileStr = storageService.getItem('profile');
        var userProfile = null;
        if (userProfileStr) {
            try {
                userProfile = JSON.parse(userProfileStr);
            }
            catch (err) {
                loggerService.error('[ml4kauth] Invalid stored profile', err);
                clearStoredAuthItems();
            }
        }
        if (userProfile && !hasUsableStoredAuthState(userProfile)) {
            loggerService.error('[ml4kauth] Stored auth state is invalid');
            clearStoredAuthItems();
            userProfile = null;
        }
        var deferredProfile = $q.defer();

        if (userProfile) {
            loggerService.debug('[ml4kauth] Existing user profile restored');

            if (hasSessionExpired()) {
                loggerService.debug('[ml4kauth] Session expired');

                // We found an access token in local storage, but it
                // has expired, so wipe local auth state quietly. Protected
                // routes will decide whether the login page is needed.
                clearExpiredAuthState('stored profile expired');
            }
            else {
                loggerService.debug('[ml4kauth] Setting restored profile to use');

                deferredProfile.resolve(userProfile);

                $rootScope.isTeacher = (userProfile.role === 'supervisor');
                $rootScope.isSiteAdmin = (userProfile.role === 'siteadmin');
                $rootScope.isAuthenticated = true;

                if (Sentry) {
                    Sentry.configureScope(function (scope) {
                        scope.setUser({
                            email : userProfile.email,
                            username : userProfile.user_id
                        });
                        scope.setExtra('role', userProfile.role);
                        scope.setExtra('tenant', userProfile.tenant);
                    });
                }

                // Try to keep the user logged in while they're still active
                //  by refreshing their access token for them in the background
                // This can't be done for "Try it now" users, as those sessions
                //  can't be renewed.
                if (userProfile.tenant !== SESSION_USERS_CLASS && !userProfile.localPreview) {
                    syncPlatformSessionCookie();

                    var expiresAt = JSON.parse(storageService.getItem('expires_at'));
                    var refreshTime = expiresAt - TEN_MINUTES_MILLISECS;
                    var timeToRefresh = refreshTime - (new Date().getTime());
                    if (timeToRefresh > 0) {
                        loggerService.debug('[ml4kauth] Token valid for longer than 10 minutes');
                        scheduleTokenRenewal(timeToRefresh);
                    }
                    else {
                        loggerService.debug('[ml4kauth] Token due to renew within 10 minutes');

                        // the session is going to expire within 10 minutes, so
                        // we'll refresh it immediately
                        renewLogin();
                    }
                }
            }
        }


        function scheduleTokenRenewal(timeToRefreshMs) {
            loggerService.debug('[ml4kauth] Scheduling token renewal');
            nextRefreshTimer = setTimeout(renewLogin, timeToRefreshMs);
        }


        function renewLogin() {
            loggerService.debug('[ml4kauth] Renewing login');

            if (lock) {
                lock.checkSession({}, function (err, authres) {
                    if (err) {
                        loggerService.error('[ml4kauth] Failed to renew login', err);
                    }
                    else if (authres) {
                        loggerService.debug('[ml4kauth] Renewed login', authres);

                        storeToken(authres);

                        // schedule the next renewal!
                        var expiresInSeconds = authres.expiresIn;
                        var expiresInMillisecs = expiresInSeconds * 1000;
                        var timeToRefreshLogin = expiresInMillisecs - TEN_MINUTES_MILLISECS;
                        if (timeToRefreshLogin > 0) {
                            scheduleTokenRenewal(timeToRefreshLogin);
                        }
                    }
                });
            }
            else {
                loggerService.error('[ml4kauth] Unexpected call to renewLogin');
            }
        }


        function login(stateName, stateParams) {
            loggerService.debug('[ml4kauth] login');
            if (shouldUseLocalPreviewAuth() || isOidcLoginEnabled()) {
                if (stateName) {
                    rememberPostLoginState(stateName, stateParams);
                }
                else if (!storageService.getItem('post_login_state')) {
                    rememberPostLoginState();
                }
                return $state.go('login');
            }
            if (lock) {
                lock.show({
                    languageDictionary : {
                        title: '登录启探 AI 工坊'
                    }
                });
            }
            else {
                loggerService.error('[ml4kauth] Unexpected call to login');
            }
        }

        function loginWithOrganizationAccount() {
            rememberPostLoginState();
            return $state.go('login');
        }

        function reset() {
            loggerService.debug('[ml4kauth] reset');
            if (shouldUseLocalPreviewAuth() || isOidcLoginEnabled()) {
                var alert = $mdDialog.alert()
                    .title('密码重置请联系管理员')
                    .textContent('教师账号由课程负责人或平台管理员统一管理，请联系管理员重置密码或确认登录方式。')
                    .ok('好的');
                return $mdDialog.show(alert);
            }
            if (lock) {
                lock.show({
                    languageDictionary : {
                        title: '忘记密码？'
                    },

                    allowForgotPassword : true,
                    allowLogin : false,

                    initialScreen : 'forgotPassword'
                });
            }
            else {
                loggerService.error('Unexpected call to reset');
            }
        }


        function clearAuthData(options) {
            loggerService.debug('[ml4kauth] Clearing auth data');

            var hadProductContext = productContext !== null || productContextPromise !== null;
            var shouldClearPlatformSessionCookie = !options || options.clearPlatformSessionCookie !== false;
            if (shouldClearPlatformSessionCookie) {
                clearPlatformSessionCookie();
            }

            if (nextRefreshTimer) {
                loggerService.debug('[ml4kauth] Clearing token renewal timer');
                clearTimeout(nextRefreshTimer);
                nextRefreshTimer = null;
            }

            deferredProfile = $q.defer();

            clearStoredAuthItems();

            authManager.unauthenticate();

            const wasAuthenticated = $rootScope.isAuthenticated;
            const wasTeacher = $rootScope.isTeacher;
            const wasSiteAdmin = $rootScope.isSiteAdmin;

            userProfile = null;
            resetProductContext();
            $rootScope.isTeacher = false;
            $rootScope.isSiteAdmin = false;
            $rootScope.isAuthenticated = false;

            if (wasAuthenticated || wasTeacher || wasSiteAdmin || hadProductContext) {
                $rootScope.$broadcast('authStateChange', 'cleared auth data');
            }
        }

        function logout() {
            loggerService.debug('[ml4kauth] logout');

            if (userProfile && userProfile.tenant === SESSION_USERS_CLASS && isAuthManagerAuthenticatedSafely()) {
                loggerService.debug('[ml4kauth] Logging out session user');
                deleteSessionUser(userProfile.user_id)
                    .then(function () {
                        loggerService.debug('[ml4kauth] Deleted session user');
                        clearAuthData();
                        storageService.clear();

                        return browserStorageService.deleteSessionUserProjects();
                    })
                    .catch(function (err) {
                        loggerService.error('[ml4kauth] Failed to delete session user', err);
                    });
            }
            else {
                clearAuthData();
            }
        }


        function storeToken(authResult) {
            loggerService.debug('[ml4kauth] Storing token');

            var expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());

            var scopes = authResult.scope || REQUESTED_SCOPES || '';

            storageService.setItem('access_token', authResult.accessToken);
            storageService.setItem('id_token', authResult.idToken);
            storageService.setItem('expires_at', expiresAt);
            storageService.setItem('scopes', JSON.stringify(scopes));

            authManager.authenticate();
        }

        function randomString(length) {
            var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            var output = '';
            for (var idx = 0; idx < length; idx++) {
                output += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return output;
        }

        function getOidcCallbackUrl() {
            return $window.location.origin + '/callback';
        }

        function startOidcLogin() {
            return $http.get(qitanAiContractService.getAuthApiUrl('oidcConfig'))
                .then(function (resp) {
                    var config = resp.data;
                    if (!config.enabled) {
                        throw new Error('OIDC login is not configured');
                    }

                    var state = randomString(32);
                    var redirectUri = config.redirectUri || getOidcCallbackUrl();
                    storageService.setItem('oidc_state', state);
                    storageService.setItem('oidc_redirect_uri', redirectUri);

                    var params = [
                        'response_type=code',
                        'client_id=' + encodeURIComponent(config.clientId),
                        'redirect_uri=' + encodeURIComponent(redirectUri),
                        'scope=' + encodeURIComponent(config.scopes || 'openid profile email'),
                        'state=' + encodeURIComponent(state)
                    ];
                    if (config.audience) {
                        params.push('audience=' + encodeURIComponent(config.audience));
                    }

                    $window.location.href = config.authorizationEndpoint + '?' + params.join('&');
                })
                .catch(function (err) {
                    loggerService.error('[ml4kauth] OIDC login failed', err);
                    throw err;
                });
        }

        function shouldUseLocalPreviewAuth() {
            return isLocalPreviewHost() &&
                   isLocalPreviewPasswordLoginEnabled();
        }

        function finishOidcLogin(code, state) {
            var expectedState = storageService.getItem('oidc_state');
            if (!expectedState || state !== expectedState) {
                loggerService.error('[ml4kauth] OIDC state mismatch');
                storageService.removeItem('oidc_state');
                storageService.removeItem('oidc_redirect_uri');
                return $state.go('login');
            }
            var redirectUri = storageService.getItem('oidc_redirect_uri') || getOidcCallbackUrl();

            return $http.post(qitanAiContractService.getAuthApiUrl('oidcCallback'), {
                code : code,
                redirectUri : redirectUri
            }).then(function (resp) {
                var authResult = resp.data;
                resetProductContext();
                storeToken(authResult);
                vm.profile = authResult.profile;
                storeProfile(vm.profile);
                storageService.removeItem('oidc_state');
                storageService.removeItem('oidc_redirect_uri');

                return continueAfterLogin()
                    .then(function () {
                        $rootScope.$broadcast('authStateChange', 'oidc authentication complete');
                    });
            }).catch(function (err) {
                loggerService.error('[ml4kauth] OIDC callback failed', err);
                storageService.removeItem('oidc_state');
                storageService.removeItem('oidc_redirect_uri');
                logout();
                $state.go('login');
            });
        }

        function storeProfile(profile) {
            loggerService.debug('[ml4kauth] Storing profile');

            userProfile = profile;

            storageService.setItem('profile', JSON.stringify(userProfile));
            deferredProfile.resolve(userProfile);

            $rootScope.isTeacher = (userProfile.role === 'supervisor');
            $rootScope.isSiteAdmin = (userProfile.role === 'siteadmin');
            $rootScope.isAuthenticated = true;

            if (userProfile.tenant !== SESSION_USERS_CLASS && !userProfile.localPreview) {
                syncPlatformSessionCookie();
            }
        }


        function extractAppMetadata(profile) {
            loggerService.debug('[ml4kauth] Extracting app metadata from profile data');

            var qitanClaims = 'https://qitan-ai-workshop.local/api/';
            var legacyClaims = 'https://' + [ 'machinelearningforkids', 'co', 'uk' ].join('.') + '/api/';
            var tenant = profile[qitanClaims + 'tenant'] ||
                         profile[legacyClaims + 'tenant'];
            var role = profile[qitanClaims + 'role'] ||
                       profile[legacyClaims + 'role'];
            var groups = profile[qitanClaims + 'groups'] ||
                         profile[legacyClaims + 'groups'];
            var user_id = profile.sub;
            profile.tenant = tenant;
            profile.role = role;
            profile.groups = groups;
            profile.user_id = user_id;
            delete profile[qitanClaims + 'tenant'];
            delete profile[qitanClaims + 'role'];
            delete profile[qitanClaims + 'groups'];
            delete profile[legacyClaims + 'tenant'];
            delete profile[legacyClaims + 'role'];
            delete profile[legacyClaims + 'groups'];
            delete profile.sub;
            delete profile.picture;
            return profile;
        }


        function setupAuth() {
            loggerService.debug('[ml4kauth] Setting up auth');

            if (isOidcLoginEnabled()) {
                var params = parseUrlParams($window.location.search && $window.location.search[0] === '?' ?
                    $window.location.search.substring(1) :
                    '');
                if (params.code) {
                    finishOidcLogin(params.code, params.state || '');
                    return;
                }
            }

            if (!isOidcLoginEnabled() && lock) {
                loggerService.debug('[ml4kauth] Registering url interceptor');
                lock.interceptHash();

                lock.on('authenticated', function (authResult) {
                    loggerService.debug('[ml4kauth] Authenticated');

                    if (authResult && authResult.accessToken && authResult.idToken) {
                        loggerService.debug('[ml4kauth] Received expected auth tokens');

                        resetProductContext();
                        storeToken(authResult);

                        loggerService.debug('[ml4kauth] Retrieving user info');
                        lock.getUserInfo(authResult.accessToken, function (err, profile) {
                            if (err) {
                                loggerService.error('[ml4kauth] lock auth failure', err);
                                return logout();
                            }

                            loggerService.debug('[ml4kauth] Processing retrieved profile');
                            vm.profile = extractAppMetadata(profile);
                            storeProfile(vm.profile);

                            // schedule a refresh of the token a little before
                            //  it is due to expire
                            var expiresInSeconds = authResult.expiresIn;
                            var expiresInMillisecs = expiresInSeconds * 1000;
                            var timeToRefreshLogin = expiresInMillisecs - TEN_MINUTES_MILLISECS;
                            scheduleTokenRenewal(timeToRefreshLogin);

                            loggerService.debug('[ml4kauth] Redirecting after authentication');
                            $timeout(function () {
                                return continueAfterLogin();
                            })
                                .then(function () {
                                    $rootScope.$broadcast('authStateChange', 'authentication complete');
                                })
                                .catch(function (redirectErr) {
                                    loggerService.error('[ml4kauth] Failed to continue after authentication', redirectErr);
                                    logout();
                                    $state.go('login');
                                });
                        });
                    }
                    else {
                        loggerService.error('[ml4kauth] Authenticated without expected tokens');
                        loggerService.error(authResult);
                    }
                });

                lock.on('authorization_error', function (err) {
                    loggerService.warn('[ml4kauth] Authorization error', err);

                    if (err && err.errorDescription) {
                        if (err.errorDescription === 'Please verify your email to activate your class account') {
                            alert('Please verify your email to activate your class account\n\n' +
                                'When you created your account, you should have been sent an email to verify your address. \n' +
                                'Clicking on the link in that email will activate your class account.\n\n' +
                                'Please click on the Help tab for more info');
                        }
                    }
                    $timeout(function () {
                        $rootScope.$broadcast('authStateChange', 'authorization error');
                    });
                });

                // auth0 looks completely broken so try starting again
                lock.on('unrecoverable_error', function (err) {
                    loggerService.error('[ml4kauth] Unrecoverable auth error', err);

                    logout();
                    return $window.location.reload(true);
                });
            }

            // after a session has expired, tell the user what happened
            $rootScope.$on('tokenHasExpired', sessionExpired);
        }


        function sessionExpired() {
            loggerService.debug('[ml4kauth] Session has expired');

            clearAuthData();

            var alert = $mdDialog.alert()
                                .title('登录已过期')
                                .textContent('请重新登录。')
                                .ok('好的');
            $mdDialog.show(alert).finally(function () {
                $state.go('login');
            });
        }


        function handleUnauthenticated() {
            loggerService.debug('[ml4kauth] Unauthenticated event');

            if (hasSessionExpired()) {
                loggerService.debug('[ml4kauth] Unauthenticated because session has expired');
                sessionExpired();
            }
            else {
                loggerService.debug('[ml4kauth] Unexpected unauth event');
                clearAuthData();
                $state.go('login');
            }
        }

        function getProfileDeferred() {
            return deferredProfile.promise;
        }

        function getProfile() {
            return userProfile;
        }

        function isAuthenticated() {
            loggerService.debug('[ml4kauth] isAuthenticated');

            if (userProfile) {
                // Check whether the current time is past the
                // access token's expiry time
                var expired = hasSessionExpired();
                if (expired) {
                    clearExpiredAuthState('isAuthenticated check');
                }
                return !expired;
            }
            return false;
        }

        function hasSessionExpired() {
            var expired = false;
            if (userProfile) {
                // Check whether the current time is past the
                // access token's expiry time
                var expiresAt = parseStoredExpiry();
                expired = !expiresAt || (new Date().getTime() > expiresAt);
            }
            loggerService.debug('[ml4kauth] hasSessionExpired : ' + expired);
            return expired;
        }

        function parseStoredExpiry() {
            try {
                var expiresAt = JSON.parse(storageService.getItem('expires_at'));
                return typeof expiresAt === 'number' && isFinite(expiresAt) ? expiresAt : 0;
            }
            catch (err) {
                loggerService.error('[ml4kauth] Invalid stored expiry', err);
                return 0;
            }
        }

        function rememberPostLoginState(stateName, stateParams) {
            var destinationName = stateName ||
                ($state.current && $state.current.name);

            if (destinationName && destinationName !== 'login') {
                storageService.setItem('post_login_state', JSON.stringify({
                    name : destinationName,
                    params : stateParams || {}
                }));
            }
        }

        function clearExpiredAuthState(reason) {
            loggerService.debug('[ml4kauth] Clearing expired auth state: ' + reason);
            var wasSessionUser = userProfile && userProfile.tenant === SESSION_USERS_CLASS;

            clearAuthData();

            if (wasSessionUser) {
                browserStorageService.deleteSessionUserProjects()
                    .catch(function (err) {
                        loggerService.error('[ml4kauth] Failed to clear expired session user projects', err);
                    });
            }
        }

        function consumePostLoginState() {
            var state = storageService.getItem('post_login_state');
            storageService.removeItem('post_login_state');
            if (!state) {
                return null;
            }
            try {
                var parsed = JSON.parse(state);
                if (parsed && parsed.name) {
                    return parsed;
                }
            }
            catch (err) {
                // Older sessions stored only the state name.
            }
            return state;
        }

        function clearStoredAuthItems() {
            loggerService.debug('[ml4kauth] Clearing stored token');
            storageService.removeItem('access_token');
            storageService.removeItem('id_token');
            storageService.removeItem('expires_at');
            storageService.removeItem('scopes');
            storageService.removeItem('profile');
            storageService.removeItem('post_login_state');
        }

        function syncPlatformSessionCookie(options) {
            options = options || {};
            if (!isOidcLoginEnabled() || isBootstrapAdminProfile(userProfile)) {
                return $q.when();
            }
            var idToken = storageService.getItem('id_token');
            if (!isJwtLike(idToken)) {
                if (options.required) {
                    clearExpiredAuthState('missing platform session token');
                    return $q.reject({
                        message : '登录状态需要刷新，请重新登录后再进入工作台。',
                        requiresLogin : true
                    });
                }
                return $q.when();
            }
            return $http.post(qitanAiContractService.getAuthApiUrl('oidcSession'), {})
                .catch(function (err) {
                    loggerService.error('[ml4kauth] Failed to sync platform session cookie', err);
                    if (options.required) {
                        if (err && (err.status === 401 || err.status === 403)) {
                            clearExpiredAuthState('platform session cookie rejected');
                            err.requiresLogin = true;
                        }
                        return $q.reject(err);
                    }
                });
        }

        function requirePlatformSessionCookie() {
            return syncPlatformSessionCookie({ required : true });
        }

        function isBootstrapAdminProfile(profile) {
            return Boolean(profile &&
                profile.user_id === 'qitan-bootstrap-siteadmin' &&
                profile.role === 'siteadmin');
        }

        function clearPlatformSessionCookie() {
            if (!isOidcLoginEnabled()) {
                return;
            }
            $http.post(qitanAiContractService.getAuthApiUrl('oidcLogout'), {})
                .catch(function (err) {
                    loggerService.error('[ml4kauth] Failed to clear platform session cookie', err);
                });
        }

        function isJwtLike(token) {
            return typeof token === 'string' && token.split('.').length === 3;
        }

        function hasUsableStoredAuthState(profile) {
            if (!profile || !profile.user_id) {
                return false;
            }
            if (!parseStoredExpiry()) {
                return false;
            }
            return isJwtLike(storageService.getItem('id_token'));
        }

        function isAuthManagerAuthenticatedSafely() {
            try {
                return authManager.isAuthenticated();
            }
            catch (err) {
                loggerService.error('[ml4kauth] Invalid stored auth token', err);
                return false;
            }
        }


        function switchToSessionUser(userinfo) {
            loggerService.debug('[ml4kauth] Switching to session user');

            if (!userinfo || typeof userinfo !== 'object' || !userinfo.id ||
                !userinfo.token || !userinfo.sessionExpiry || !isJwtLike(userinfo.jwt))
            {
                throw new Error('Invalid session user response');
            }

            // clear out any existing user/auth info
            logout();

            loggerService.debug('[ml4kauth] Storing profile data');
            storageService.setItem('access_token', userinfo.token);
            storageService.setItem('id_token', userinfo.jwt);

            var expiryTime = JSON.stringify(new Date(userinfo.sessionExpiry).getTime());
            storageService.setItem('expires_at', expiryTime);

            storageService.setItem('scopes', 'openid email');

            userProfile = {
                tenant : SESSION_USERS_CLASS,
                role : 'student',
                user_id : userinfo.id
            };

            storageService.setItem('profile', JSON.stringify(userProfile));
            deferredProfile.resolve(userProfile);

            $rootScope.isAuthenticated = true;
            $rootScope.isTeacher = false;
            $rootScope.isSiteAdmin = false;

            $rootScope.$broadcast('authStateChange', 'switched to session user');
        }

        function switchToLocalPreviewUser(userinfo) {
            loggerService.debug('[ml4kauth] Switching to local preview user');

            if (!userinfo || typeof userinfo !== 'object' || !userinfo.profile ||
                !userinfo.token || !userinfo.sessionExpiry || !isJwtLike(userinfo.jwt))
            {
                throw new Error('Invalid local preview login response');
            }

            logout();

            loggerService.debug('[ml4kauth] Storing local preview profile data');
            storageService.setItem('access_token', userinfo.token);
            storageService.setItem('id_token', userinfo.jwt);

            var expiryTime = JSON.stringify(new Date(userinfo.sessionExpiry).getTime());
            storageService.setItem('expires_at', expiryTime);
            storageService.setItem('scopes', 'openid email');

            userProfile = userinfo.profile;
            userProfile.localPreview = true;
            storeProfile(userProfile);
            authManager.authenticate();

            $rootScope.$broadcast('authStateChange', 'switched to local preview user');
        }

        function switchToPlatformUser(authResult) {
            loggerService.debug('[ml4kauth] Switching to platform user');

            if (!authResult || typeof authResult !== 'object' || !authResult.profile ||
                !authResult.accessToken || !authResult.idToken)
            {
                throw new Error('Invalid platform login response');
            }

            clearAuthData({
                clearPlatformSessionCookie : false
            });

            storeToken(authResult);
            vm.profile = authResult.profile;
            storeProfile(vm.profile);

            $rootScope.$broadcast('authStateChange', 'platform password login complete');
        }



        function createSessionUser(token) {
            loggerService.debug('[ml4kauth] Creating session user');
            return $http.post(qitanAiContractService.getAuthApiUrl('sessionUsers'), { turnstile : token })
                .then(function (resp) {
                    loggerService.debug('[ml4kauth] Session user created');

                    var sessionuser = resp.data;

                    switchToSessionUser(sessionuser);

                    return sessionuser;
                });
        }

        function loginWithPassword(username, password) {
            loggerService.debug('[ml4kauth] Logging in with platform password');

            if (shouldUseLocalPreviewAuth()) {
                return $http.post(qitanAiContractService.getAuthApiUrl('localPreviewLogin'), {
                    username : username,
                    password : password
                })
                    .then(function (resp) {
                        var userinfo = resp.data;
                        switchToLocalPreviewUser(userinfo);
                        return userinfo;
                    });
            }

            if (isOidcLoginEnabled()) {
                return $http.post(qitanAiContractService.getAuthApiUrl('oidcPasswordLogin'), {
                    username : username,
                    password : password
                })
                    .then(function (resp) {
                        var authResult = resp.data;
                        switchToPlatformUser(authResult);
                        return authResult;
                    });
            }

            return $q.reject({
                data : {
                    error : 'Platform password login is not configured'
                },
                status : 400
            });
        }

        function deleteSessionUser(userid) {
            loggerService.debug('[ml4kauth] Deleting session user');
            return $http.delete(qitanAiContractService.getAuthApiUrl('sessionUser', {
                classid : SESSION_USERS_CLASS,
                studentid : userid
            }))
                .catch(function (err) {
                    loggerService.error('[ml4kauth] Failed to delete session user', err);
                });
        }


        function parseUrlParams(input) {
            var params = {};
            if (!input) {
                return params;
            }
            input.split('&').forEach(function (str) {
                var equalsIdx = str.indexOf('=');
                var key = equalsIdx >= 0 ? str.substring(0, equalsIdx) : str;
                var val = equalsIdx >= 0 ? str.substring(equalsIdx + 1) : '';
                params[decodeURIComponent(key)] = decodeURIComponent(val.replace(/\+/g, ' '));
            });
            return params;
        }


        function checkForAuthMessagesInUrl() {
            var paramStr = $window.location.search;
            if (paramStr &&
                paramStr[0] === '?')
            {
                var params = parseUrlParams(paramStr.substring(1));

                if (params.message === 'Your%20email%20was%20verified.%20You%20can%20continue%20using%20the%20application.')
                {
                    var alert = $mdDialog.alert()
                                    .title('欢迎来到启探 AI 工坊')
                                    .textContent('你的邮箱地址已经验证成功。')
                                    .ok('好的');
                    $mdDialog.show(alert).finally(function () {
                        $window.location = '/';
                    });
                }
            }
        }



        return {
            login : login,
            loginWithOrganizationAccount : loginWithOrganizationAccount,
            reset : reset,
            logout : logout,
            consumePostLoginState : consumePostLoginState,
            rememberStateForLogin : rememberPostLoginState,
            ensurePlatformSessionCookie : syncPlatformSessionCookie,
            requirePlatformSessionCookie : requirePlatformSessionCookie,
            isOidcLoginEnabled : isOidcLoginEnabled,
            isLocalPreviewHost : isLocalPreviewHost,
            shouldUseLocalPreviewAuth : shouldUseLocalPreviewAuth,

            setupAuth : setupAuth,
            getProfileDeferred : getProfileDeferred,
            getProfile : getProfile,
            getProductContext : getProductContext,
            getCurrentProductContext : getCurrentProductContext,
            getDefaultStateName : getDefaultStateName,
            continueAfterLogin : continueAfterLogin,
            isAuthenticated : isAuthenticated,

            handleUnauthenticated : handleUnauthenticated,

            createSessionUser : createSessionUser,
            loginWithPassword : loginWithPassword,

            checkForAuthMessagesInUrl : checkForAuthMessagesInUrl,

            storeProfile : storeProfile
        };
    }
})();
